import {
  batchUnarchiveEmails,
  getPaginated,
  unarchiveEmail,
} from "@/services/ai";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import moment from "moment";
import { ArchiveRestore, Loader2 } from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationLink,
  PaginationNext,
} from "@/components/ui/pagination";

type bookingErrorType =
  | "missingAlias"
  | "creationDate"
  | "cancellation"
  | "checkInOut"
  | "refMissing"
  | "priceMismatch"
  | "tooLong"
  | "late"
  | "unknown";

type bookingEmailStatus = "pending" | "success" | "error";

export const parsedContentSchema = z.object({
  _createdAt: z.string(),
  collectionId: z.string(),
  lang: z.string(),
  owner: z.string(),
  originalPrice: z.number(),
  lockDate: z.string(),
  guests: z
    .object({
      firstName: z.string(),
      secondName: z.string(),
      email: z.string(),
    })
    .array(),
  payload: z.object({
    reference: z.string(),
    checkin: z.string(),
    checkout: z.string(),
    board: z.string().optional(),
    rooms: z
      .object({
        name: z.string(),
        guestsAdults: z.number(),
        guestsKids: z.number(),
        amenities: z.string(),
      })
      .array(),

    extra: z.string().optional(),
  }),
  bookedWithLoyaltyCode: z.string().optional(),
  reservationType: z.string(),
});

export type bookingEmailType = {
  _id: string;
  _createdAt: Date;
  emailId: string;
  encryptedContent: { headers: { subject: string }; text: string };
  hashedContent: string;
  step: number;
  status: bookingEmailStatus;
  errorType?: bookingErrorType;
  hotelAlias: string;
  encryptedParsedContent:
    | z.infer<typeof parsedContentSchema>
    | "No data"
    | "Ofuscated data";
  expiresAt: Date;
};

const ArchivedAi = () => {
  const [emails, setEmails] = useState<bookingEmailType[]>();
  const [emailsLoading, setEmailsLoading] = useState(true);
  const [emailsPage, setEmailsPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [shiftHeld, setShiftHeld] = useState(false);

  function downHandler(e: KeyboardEvent) {
    if (e.key === "Shift") {
      setShiftHeld(true);
    }
  }

  function upHandler(e: KeyboardEvent) {
    if (e.key === "Shift") {
      setShiftHeld(false);
    }
  }

  useEffect(() => {
    window.addEventListener("keydown", downHandler);
    window.addEventListener("keyup", upHandler);
    return () => {
      window.removeEventListener("keydown", downHandler);
      window.removeEventListener("keyup", upHandler);
    };
  }, []);

  const fetchEmails = async (page: number) => {
    setEmailsLoading(true);
    try {
      const res = await getPaginated({
        page: page,
        size: 10,
        startDate: new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000),
        labels: ["archived"],
      });
      console.log(res);
      setEmails(res.data);
      setEmailsPage(res.page);
      setTotalPages(res.total / res.size);
    } catch {
      setEmails(undefined);
    } finally {
      setEmailsLoading(false);
    }
  };

  useEffect(() => {
    fetchEmails(emailsPage);
  }, [emailsPage]);

  if (emailsLoading) {
    return (
      <div className="flex flex-col justify-center items-center w-full h-full gap-6">
        <p>Sto raccogliendo i dati</p>
        <Loader2 className="mr-2 h-12 w-12 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-12">
      <div>
        <Link to="/admin/ai">Indietro</Link>
      </div>
      <p className="text-3xl">Archivio</p>
      {emails && emails.length > 0 ? (
        <>
          <div className="flex flex-col gap-4">
            <div className="flex gap-6 items-center">
              <div className="flex gap-2 items-center">
                <input
                  type="checkbox"
                  className="cursor-pointer"
                  checked={selectedIds.length === emails.length}
                  onChange={(e) => {
                    if (!emails) return;
                    if (e.target.checked) {
                      setSelectedIds(emails.map((email) => email._id));
                      return;
                    }
                    setSelectedIds([]);
                  }}
                />
                <p>Seleziona tutti</p>
              </div>

              <button
                className="flex gap-2 items-center border shadow-lg rounded-2xl px-2 py-1"
                onClick={async (event) => {
                  event.stopPropagation();
                  if (
                    confirm("Clicca conferma per disarchiviare i selezionati")
                  ) {
                    await batchUnarchiveEmails(selectedIds);
                    fetchEmails(emailsPage);
                  }
                }}
                style={{
                  opacity: selectedIds.length === 0 ? 0.5 : 1,
                  cursor: selectedIds.length === 0 ? "unset" : "pointer",
                }}
                disabled={selectedIds.length === 0}
              >
                <ArchiveRestore className="h-4 w-4" />
                <p>Disarchiva selezionati</p>
              </button>
            </div>
            {emails.map((email, index) => (
              <div className="p-2 shadow-lg rounded-lg cursor-pointer flex items-center justify-between gap-2">
                <div className="flex gap-2">
                  <input
                    type="checkbox"
                    className="cursor-pointer"
                    checked={selectedIds.includes(email._id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds((p) => {
                          const candidates: string[] = [];
                          let isBroken = false;
                          if (shiftHeld) {
                            for (let i = index - 1; i >= 0; i--) {
                              if (p.includes(emails[i]._id)) {
                                isBroken = true;
                                break;
                              }
                              candidates.push(emails[i]._id);
                            }
                          }
                          return [
                            ...p,
                            ...(isBroken ? candidates : []),
                            email._id,
                          ];
                        });
                        return;
                      }
                      if (!shiftHeld) {
                        setSelectedIds((p) =>
                          p.filter((id) => id !== email._id)
                        );
                      }
                    }}
                  />
                  <Link
                    className="flex items-center gap-2"
                    to={`/admin/ai/${email._id}`}
                    target="_blank"
                  >
                    <div
                      className="px-2 py-1 rounded-lg"
                      style={{
                        backgroundColor:
                          email.status === "success"
                            ? "#9AFEE3"
                            : email.status === "error"
                            ? "#ff746c"
                            : "#ddd",
                      }}
                    >
                      <p>
                        #
                        {email.status === "error"
                          ? email.errorType
                          : email.status}
                      </p>
                    </div>
                    <p>{email.encryptedContent.headers.subject}</p>
                  </Link>
                </div>
                <div className="flex gap-4">
                  <p className="text-xs text-gray-500">
                    {moment(email._createdAt).format("DD/MM/YYYY hh:mm:ss")}
                  </p>
                  <button
                    onClick={async (event) => {
                      event.stopPropagation();
                      if (confirm("Clicca conferma per disarchiviare")) {
                        await unarchiveEmail(email._id);
                        fetchEmails(emailsPage);
                        return;
                      }
                    }}
                    className="z-10"
                  >
                    <ArchiveRestore className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={() => setEmailsPage((p) => Math.max(p - 1, 1))}
                />
              </PaginationItem>

              {Array.from({ length: totalPages }, (_, i) => (
                <PaginationItem key={i}>
                  <PaginationLink
                    href="#"
                    isActive={emailsPage === i + 1}
                    onClick={() => setEmailsPage(i + 1)}
                  >
                    {i + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}

              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={() =>
                    setEmailsPage((p) => Math.min(p + 1, totalPages))
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </>
      ) : null}
    </div>
  );
};

export default ArchivedAi;
